import React, { useRef } from "react";
import {
  Box,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import {
  SpaOutlined,
  TrendingUp,
  MonetizationOnOutlined,
  Settings,
  CloudQueueOutlined,
} from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";

import { useAppState } from "context/appState.context";
import { ProjectRegisterMetrics } from "constants/constant";
import Spinner from "components/Spinner";
import { _getMultipleLocationProjectRegisterDetails } from "middlewares/DashboardApi/portfolioView";
import MarginalAbatementCostCurve, { MarginalAbatementCostCurveRef } from "pages/Dashboards/ProjectRegister/MarginalAbatementCostCurve";
import MeasureImpactsbyScope, { MeasureImpactsbyScopeRef } from "pages/Dashboards/ProjectRegister/MeasureImpactsbyScope";
import { MACgraphModel } from "pages/Dashboards/ProjectRegister/data/MACgraphModel";
import { MIBScopeData } from "pages/Dashboards/ProjectRegister/data/MIBScopeData";
import { TLFMetricsData } from "pages/Dashboards/ProjectRegister/data/TLFMetricsData";
import { generateLocationLevelData } from "pages/Dashboards/ProjectRegister/data/metricsUtils";
import TimelineAndFinancialMetrics, { TimelineAndFinancialMetricsRef } from "pages/Dashboards/ProjectRegister/TimelineAndFinancialMetrics";
import {
  ProjectRegisterResult,
  WaterfallGroupModel,
} from "./Data/ProjectRegisterResult";
import { ProjectRegisterFilter } from "./Data/ProjectRegisterFilter";
import { PortfolioGlidepathResult } from "./Data/PortfolioGlidepathModels";
import PortfolioGlidepath, { PortfolioGlidepathRef } from "./ProjectRegister/PortfolioGlidepath";
import PortfolioSummaryTable from "./ProjectRegister/PortfolioSummaryTable";
import { LocationScenarioFilter } from "./Data/ProjectRegisterFilter";
import ChartDownloadButtonMenu from "components/ChartDownloadButtons/ChartDownloadButtons";
import { normalizeMeasureLabel } from "pages/Dashboards/ProjectRegister/data/measureLabelUtils";
import { formatEmissionValue } from "utilities/utilities";

interface DashboardMetric {
  label: string;
  value: string;
  icon: React.ElementType;
}

export interface ProjectRegisterSectionProps {
  locationIds: number[];
  scopeFilter: string[];
  isClientView?: boolean;
  activeTab: "baseline" | "project";
  searchVersion?: number;
  portfolioGlidepathData?: PortfolioGlidepathResult | null;
  groupBy?: string;
  onGroupByChange?: (v: string) => void;
  locationScenarios?: LocationScenarioFilter[];
}

const INITIAL_METRICS: DashboardMetric[] = [
  { label: "Emission Reduction (tCO2e)", value: "0", icon: SpaOutlined },
  { label: "Annual NPV ($)", value: "0", icon: TrendingUp },
  { label: "CAPEX Investment ($)", value: "0", icon: MonetizationOnOutlined },
  { label: "OPEX Investment ($)", value: "0", icon: Settings },
  { label: "Avg MAC ($/tCO2e)", value: "0", icon: CloudQueueOutlined },
];

const CLIENT_DEFAULT_CHART = ProjectRegisterMetrics.MarginalAbatementCostCurve;
const NON_CLIENT_DEFAULT_CHART = ProjectRegisterMetrics.MarginalAbatementCostCurve;
const CLIENT_DEFAULT_WATERFALL_GROUP_BY = "Organization";
const NON_CLIENT_DEFAULT_WATERFALL_GROUP_BY = "Location";

const CLIENT_WATERFALL_GROUP_BY_OPTIONS = [
  { label: "Organization", value: "Organization" },
  { label: "Measure Type", value: "Type" },
  { label: "Measure Category", value: "Category" },
];

const NON_CLIENT_WATERFALL_GROUP_BY_OPTIONS = [
  { label: "Location", value: "Location" },
  { label: "Measure Type", value: "Type" },
  { label: "Measure Category", value: "Category" },
  { label: "Individual Measures", value: "Individual" },
];

const NON_MEASURE_WATERFALL_LABELS = new Set(
  ["BAU Emissions", "Residual emissions", "Historical Emissions", "Growth"].map(
    normalizeMeasureLabel
  )
);

const selectSx = {
  borderRadius: "6px",
  fontSize: "0.85rem",
  backgroundColor: "#fff",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
};

const menuItemSx = { fontSize: "0.85rem" };

const mapWaterfallGroupByToFilterValue = (groupBy: string): string => {
  switch (groupBy) {
    case "Organization":
      return "organization";
    case "Location":
      return "location";
    case "Category":
      return "measureCategory";
    case "Individual":
      return "individual";
    case "Type":
    default:
      return "measureType";
  }
};

// Reverse: API-level groupBy value → waterfall display value used by MeasureImpactsbyScope
const mapFilterValueToWaterfallGroupBy = (filterValue: string): string => {
  switch (filterValue) {
    case "organization": return "Organization";
    case "location":     return "Location";
    case "measureCategory": return "Category";
    case "individual":   return "Individual";
    case "measureType":
    default:             return "Type";
  }
};

const mapImpactByScopeItem = (item: any): MIBScopeData => ({
  label:
    item.groupLabel ??
    item.locationName ??
    item.reductionMeasureName ??
    item.measureTypeName ??
    item.measureCategoryName ??
    item.label ??
    "Unknown",
  value: item.emissionReduction ?? 0,
  barType: "reduction",
  measures: item.measures ?? [],
  organizations: item.organizations ?? [],
  scopeByEmissions: item.scopeByEmissions ?? [],
});

const buildWaterfallChartData = (res: ProjectRegisterResult): MIBScopeData[] => {
  const waterfallGroups = res.waterfallGroups ?? [];
  const baseline = res.waterfallBaseline;

  if (!baseline || waterfallGroups.length === 0) {
    return [];
  }

  const chartData: MIBScopeData[] = [];

  // Start bar: BAU Emissions
  chartData.push({
    label: "BAU Emissions",
    value: baseline.bauEmissions ?? 0,
    barType: "start",
    color: "#A9A9A9",
    measures: [],
  });
  if (baseline.historicalEmissions != null) {
    chartData.push({
      label: "Historical Emissions",
      value: baseline.historicalEmissions,
      barType: "historical",
      color: "#5e4bea",
      measures: [],
    });
  }

  if (baseline.growthEmissions != null) {
    chartData.push({
      label: "Growth",
      value: baseline.growthEmissions,
      barType: "growth",
      color: "#A020F0",
      measures: [],
    });
  }

  // Measure groups
  waterfallGroups.forEach((group: WaterfallGroupModel) => {
    chartData.push({
      label: group.groupLabel ?? "Unknown",
      value: group.emissionReduction ?? 0,
      barType: "reduction",
      measures: group.measures ?? [],
      organizations: group.organizations ?? [],
    });
  });

  chartData.push({
    label: "Residual emissions",
    value: baseline.residualEmissions ?? 0,
    barType: "end",
    color: "#A9A9A9",
    measures: [],
    scopeByEmissions: baseline.scopeByEmissions ?? [],
  });

  return chartData;
};

const ProjectRegisterSection: React.FC<ProjectRegisterSectionProps> = ({
  locationIds,
  scopeFilter,
  isClientView,
  activeTab,
  searchVersion = 0,
  portfolioGlidepathData,
  groupBy,
  onGroupByChange,
  locationScenarios,
}) => {
  const { currency } = useAppState();

  // chartGroupBy is lifted to the parent (PortfolioView) and passed as groupBy prop.
  // Fallback to view-appropriate default when the prop is absent (e.g. in tests).
  const chartGroupBy = groupBy ?? (isClientView ? "organization" : "location");

  // Ref so the effect can always read the CURRENT groupBy value without it being a
  // dependency — changes apply only when Search is clicked (searchVersion increments).
  const chartGroupByRef = React.useRef(chartGroupBy);
  chartGroupByRef.current = chartGroupBy;

  const [chartType, setChartType] = React.useState<string>(
    isClientView ? CLIENT_DEFAULT_CHART : NON_CLIENT_DEFAULT_CHART
  );

  const [loading, setLoading] = React.useState(false);

  const [dashboardMetrics, setDashboardMetrics] = React.useState<DashboardMetric[]>(INITIAL_METRICS);
  const [macData, setMacData] = React.useState<MACgraphModel[]>([]);
  const [mibScopeData, setMibScopeData] = React.useState<MIBScopeData[]>([]);
  const [tlfMetricsData, setTlfMetricsData] = React.useState<TLFMetricsData[]>([]);
  const [isMacDataLoaded, setIsMacDataLoaded] = React.useState(false);
  const [isDataLoaded, setIsDataLoaded] = React.useState(false);

  const chartMeasureOrder = React.useMemo(() => {
    const safeMacData = Array.isArray(macData) ? macData : [];
    const safeTimelineData = Array.isArray(tlfMetricsData) ? tlfMetricsData : [];
    const safeWaterfallData = Array.isArray(mibScopeData) ? mibScopeData : [];

    if (chartType === ProjectRegisterMetrics.MarginalAbatementCostCurve) {
      return safeMacData
        .map((item) => normalizeMeasureLabel(item?.locationName))
        .filter((label) => label.length > 0);
    }

    if (chartType === ProjectRegisterMetrics.TimelineandFinancialMetrics) {
      const firstDataPoint = safeTimelineData[0];
      if (!firstDataPoint) {
        return [];
      }

      return Object.keys(firstDataPoint)
        .filter((key) => key !== "year")
        .map((key) => normalizeMeasureLabel(key))
        .filter((label) => label.length > 0);
    }

    if (chartType === ProjectRegisterMetrics.MeasureImpactsbyScope) {
      return safeWaterfallData
        .map((item) => ({
          label: normalizeMeasureLabel(item?.label),
          barType: item?.barType,
        }))
        .filter(({ label, barType }) => {
          if (barType && barType !== "reduction") {
            return false;
          }

          return label.length > 0 && !NON_MEASURE_WATERFALL_LABELS.has(label);
        })
        .map(({ label }) => label);
    }

    return [];
  }, [chartType, macData, tlfMetricsData, mibScopeData]);

  const hasLoadedProjectData = React.useRef(false);
  const lastCalledFilter = React.useRef<ProjectRegisterFilter | null>(null);
  const lastSearchVersion = React.useRef(0);
  const isFilterChangedRef = React.useRef(false);

  // Refs to track latest filter prop values without making them effect dependencies.
  // This prevents dropdown changes from triggering API calls — only Search button should.
  const locationIdsRef = React.useRef(locationIds);
  const scopeFilterRef = React.useRef(scopeFilter);
  const locationScenariosRef = React.useRef(locationScenarios);
  locationIdsRef.current = locationIds;
  scopeFilterRef.current = scopeFilter;
  locationScenariosRef.current = locationScenarios;

  // Chart refs for imperative handle (download)
  const macCurveRef = useRef<MarginalAbatementCostCurveRef>(null);
  const measureImpactsRef = useRef<MeasureImpactsbyScopeRef>(null);
  const timelineFinancialRef = useRef<TimelineAndFinancialMetricsRef>(null);
  const portfolioGlidepathRef = useRef<PortfolioGlidepathRef>(null);

  // Download handler
  const handleDownload = (format: "png" | "jpeg") => {
    if (chartType === ProjectRegisterMetrics.MarginalAbatementCostCurve && macCurveRef.current) {
      macCurveRef.current.downloadChart(format);
    }
    if (chartType === ProjectRegisterMetrics.MeasureImpactsbyScope && measureImpactsRef.current) {
      measureImpactsRef.current.downloadChart(format);
    }
    if (chartType === ProjectRegisterMetrics.TimelineandFinancialMetrics && timelineFinancialRef.current) {
      timelineFinancialRef.current.downloadChart(format);
    }
    if (chartType === ProjectRegisterMetrics.InteractiveGlidepath && portfolioGlidepathRef.current) {
      portfolioGlidepathRef.current.downloadChart(format);
    }
  };

  const getRequestedDashboardSection = React.useCallback(
    (selectedChartType: string): string => {
      if (selectedChartType === ProjectRegisterMetrics.InteractiveGlidepath) {  
        return ProjectRegisterMetrics.MarginalAbatementCostCurve;
      }
      return selectedChartType;
    },
    []
  );

  const getRequestedGroupBy = React.useCallback(
    (_selectedChartType: string): string => {
      return chartGroupByRef.current;
    },
    []
  );

  async function fetchProjectRegisterData(filter: ProjectRegisterFilter) {
    setLoading(true);
    setIsMacDataLoaded(false);
    setIsDataLoaded(false);
    hasLoadedProjectData.current = true;
    lastCalledFilter.current = filter;
    try {
      const res = await _getMultipleLocationProjectRegisterDetails({ ...filter, isFilterChanged: isFilterChangedRef.current });
      handleResponse(res, filter.dashboardSection ?? ProjectRegisterMetrics.MarginalAbatementCostCurve);
    } catch (err) {
      console.error("Error fetching project register data:", err);
      setLoading(false);
    }
  }

  function handleResponse(res: ProjectRegisterResult | null, dashboardSection: string) {
    if (res) {
      setDashboardMetrics((prev) => {
        const updated = [...prev];
        updated[0] = { ...updated[0], value: String(res.dashboardMetric?.emissionReduction ?? "0") };
        updated[1] = { ...updated[1], value: String(res.dashboardMetric?.annualNPV ?? "0") };
        updated[2] = { ...updated[2], value: String(res.dashboardMetric?.capexInvestment ?? "0") };
        updated[3] = { ...updated[3], value: String(res.dashboardMetric?.opexInvestment ?? "0") };
        updated[4] = { ...updated[4], value: res.dashboardMetric?.mac !== undefined ? res.dashboardMetric.mac.toFixed(2) : "0" };
        return updated;
      });

      // macData is always updated — it drives both the MAC chart and the Summary Table.
      setMacData(res.locationLevelMACGraphModelList ?? []);
      setIsMacDataLoaded(true);
      if (dashboardSection === ProjectRegisterMetrics.TimelineandFinancialMetrics) {
        setTlfMetricsData(generateLocationLevelData(res));
        setIsDataLoaded(true);
      }
      if (dashboardSection === ProjectRegisterMetrics.MeasureImpactsbyScope) {
        // Use new waterfallGroups API structure if available (production)
        if (res.waterfallGroups && res.waterfallBaseline) {
          setMibScopeData(buildWaterfallChartData(res));
        } else if (res.impactByScopeModelList) {
          // Fallback to old structure for backward compatibility
          setMibScopeData((res.impactByScopeModelList ?? []).map(mapImpactByScopeItem));
        } else {
          setMibScopeData([]);
        }
      }
    }
    setLoading(false);
    isFilterChangedRef.current = false;
  }

  const fetchWaterfallData = React.useCallback(
    async (groupBy: string): Promise<MIBScopeData[]> => {
      const currentLocationIds = locationIdsRef.current;
      if (currentLocationIds.length === 0) return [];

      try {
        const response = await _getMultipleLocationProjectRegisterDetails({
          locationId: currentLocationIds,
          scopes: scopeFilterRef.current,
          dashboardSection: ProjectRegisterMetrics.MeasureImpactsbyScope,
          groupBy: mapWaterfallGroupByToFilterValue(groupBy),
        });

        if (response?.waterfallGroups && response?.waterfallBaseline) {
          return buildWaterfallChartData(response);
        }

        return (response?.impactByScopeModelList ?? []).map(mapImpactByScopeItem);
      } catch (err) {
        console.error("Error fetching waterfall data:", err);
        return [];
      }
    },
    []
  );

  const renderClientChartContent = (): React.ReactNode => {
    if (loading && chartType !== ProjectRegisterMetrics.InteractiveGlidepath) {
      return <Spinner size={80} data-testid="spinner" />;
    }

    if (chartType === ProjectRegisterMetrics.MarginalAbatementCostCurve) {
      return isMacDataLoaded ? (
        <div>
          <MarginalAbatementCostCurve ref={macCurveRef} data={macData} labelKey="locationName" currency={currency} />
        </div>
      ) : null;
    }

    if (chartType === ProjectRegisterMetrics.InteractiveGlidepath) {
      return portfolioGlidepathData ? (
        <PortfolioGlidepath ref={portfolioGlidepathRef} data={portfolioGlidepathData} />
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 320 }}>
          <CircularProgress size={60} sx={{ color: "#E57A1F" }} />
        </Box>
      );
    }

    if (chartType === ProjectRegisterMetrics.TimelineandFinancialMetrics) {
      return isDataLoaded ? (
        <TimelineAndFinancialMetrics
          ref={timelineFinancialRef}
          data={tlfMetricsData}
          width={1100}
          height={450}
        />
      ) : null;
    }

    if (chartType === ProjectRegisterMetrics.MeasureImpactsbyScope) {
      return (
      <Grid container sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
        <MeasureImpactsbyScope
          ref={measureImpactsRef}
          data={mibScopeData}
          currency={currency}
          groupBy={mapFilterValueToWaterfallGroupBy(chartGroupBy)}
          groupByOptions={CLIENT_WATERFALL_GROUP_BY_OPTIONS}
          fitContainerWidth={true}
          fetchData={fetchWaterfallData}
        />
      </Grid>
      );
    }

    return null;
  };

  const renderChartTypeSelect = () => (
    <FormControl variant="outlined" size="small" sx={{ minWidth: 220 }}>
      <Select
        value={chartType}
        onChange={(e) => setChartType(e.target.value)}
        sx={selectSx}
      >
        <MenuItem value={ProjectRegisterMetrics.MarginalAbatementCostCurve} sx={menuItemSx}>Marginal Abatement Cost</MenuItem>
        <MenuItem value={ProjectRegisterMetrics.TimelineandFinancialMetrics} sx={menuItemSx}>Decarbonization Pathway</MenuItem>
        <MenuItem value={ProjectRegisterMetrics.MeasureImpactsbyScope} sx={menuItemSx}>Decarbonization Waterfall</MenuItem>
        <MenuItem value={ProjectRegisterMetrics.InteractiveGlidepath} sx={menuItemSx}>Target Trajectory</MenuItem>
      </Select>
    </FormControl>
  );

  React.useEffect(() => {
    setChartType(isClientView ? CLIENT_DEFAULT_CHART : NON_CLIENT_DEFAULT_CHART);
  }, [isClientView]);

  React.useEffect(() => {
    if (activeTab !== "project") return;

    const currentLocationIds = locationIdsRef.current;
    if (currentLocationIds.length === 0) return;

    const requestedDashboardSection = getRequestedDashboardSection(chartType);

    const filter: ProjectRegisterFilter = {
      locationId: currentLocationIds,
      scopes: scopeFilterRef.current,
      dashboardSection: requestedDashboardSection,
      groupBy: getRequestedGroupBy(chartType),
      locationScenarios: locationScenariosRef.current,
    };
    const apiSupportedChartTypes = [
      ProjectRegisterMetrics.MarginalAbatementCostCurve,
      ProjectRegisterMetrics.MeasureImpactsbyScope,
      ProjectRegisterMetrics.TimelineandFinancialMetrics,
    ];
    if (!apiSupportedChartTypes.includes(requestedDashboardSection)) return;
    // Search button explicitly clicked → always re-fetch with current filter values
    if (searchVersion !== lastSearchVersion.current) {
      lastSearchVersion.current = searchVersion;
      isFilterChangedRef.current = true;
      fetchProjectRegisterData(filter);
      return;
    }

    // First visit to project tab → fetch
    if (!hasLoadedProjectData.current) {
      fetchProjectRegisterData(filter);
      return;
    }

    // Compare current filter values against last called — catches filter changes made while on another tab
    const last = lastCalledFilter.current;

    const numericSort = (a: number, b: number) => a - b;
    const stringSort = (a: string, b: string) => a.localeCompare(b);
    const filtersChanged =
      !last ||
      requestedDashboardSection !== last.dashboardSection ||
      filter.groupBy !== last.groupBy ||
      JSON.stringify([...(filter.locationId ?? [])].sort(numericSort)) !== JSON.stringify([...(last.locationId ?? [])].sort(numericSort)) ||
      JSON.stringify([...(filter.scopes ?? [])].sort(stringSort)) !== JSON.stringify([...(last.scopes ?? [])].sort(stringSort)) ||
      JSON.stringify([...(filter.baseYear ?? [])].sort(numericSort)) !== JSON.stringify([...(last.baseYear ?? [])].sort(numericSort)) ||
      JSON.stringify(
        [...(filter.locationScenarios ?? [])].sort(
          (a, b) => a.locationId - b.locationId || (a.scenarioSection ?? "").localeCompare(b.scenarioSection ?? "")
        )
      ) !== JSON.stringify(
        [...(last.locationScenarios ?? [])].sort(
          (a, b) => a.locationId - b.locationId || (a.scenarioSection ?? "").localeCompare(b.scenarioSection ?? "")
        )
      );

    if (filtersChanged) {
      isFilterChangedRef.current = true;
      fetchProjectRegisterData(filter);
    }

    // Otherwise: tab revisit with no changes → skip API call
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, chartType, searchVersion, getRequestedDashboardSection, getRequestedGroupBy]);

  return (
    <Grid container spacing={1.5}>
      {/* Metric Cards */}
      {dashboardMetrics.map((metric) => {
        const MetricIcon = metric.icon;
        return (
          <Grid item xs={12} sm={6} md={2} key={metric.label}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: "18px",
                border: "1px solid #d8dde3",
                backgroundColor: "#fff",
                minHeight: 84,
                position: "relative",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Typography sx={{ fontSize: "0.80rem", color: "#7c8ca1", lineHeight: 1.25, mb: 0.5, pr: 4 }}>
                {metric.label}
              </Typography>
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#0f2b52", lineHeight: 1.1, whiteSpace: "nowrap" }}>
                {formatEmissionValue(metric.value)}
              </Typography>
              <MetricIcon sx={{ fontSize: 22, color: "#f2e2d7", position: "absolute", right: 10, bottom: 8 }} />
            </Paper>
          </Grid>
        );
      })}

      {/* Client View: Portfolio Target Trajectory */}
      {isClientView && (
        <Grid item xs={12} sx={{ minWidth: 0, overflow: "hidden" }}>
          <Paper elevation={0} sx={{ p: 2.5, mt: 1, borderRadius: "12px", border: "1px solid #d8dde3", backgroundColor: "#fff", overflow: "hidden" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "#0f2b52", whiteSpace: "nowrap" }}>
                {chartType === ProjectRegisterMetrics.InteractiveGlidepath
                  ? "Portfolio Target Trajectory"
                  : chartType}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {renderChartTypeSelect()}
                <ChartDownloadButtonMenu
                  onDownload={handleDownload}
                  buttonLabels={{ png: "Download PNG", jpeg: "Download JPEG" }}
                  sx={{ ml: 1 }}
                  tooltipText={`Download '${chartType}' chart`}
                />
              </Box>
            </Box>
            {renderClientChartContent()}
          </Paper>
        </Grid>
      )}

      {/* Portfolio Charts (Organization/Portfolio View) */}
      {!isClientView && (
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 2.5, mt: 1, borderRadius: "12px", border: "1px solid #d8dde3", backgroundColor: "#fff" }}>
            {/* Header row */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "#0f2b52", whiteSpace: "nowrap" }}>
                {
                  chartType === ProjectRegisterMetrics.MarginalAbatementCostCurve
                    ? "Marginal Abatement Cost"
                    : chartType === ProjectRegisterMetrics.TimelineandFinancialMetrics
                      ? "Decarbonization Pathway"
                      : chartType === ProjectRegisterMetrics.MeasureImpactsbyScope
                        ? "Decarbonization Waterfall"
                        : chartType === ProjectRegisterMetrics.InteractiveGlidepath
                          ? "Target Trajectory"
                          : "Portfolio Charts"
                }
                </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {renderChartTypeSelect()}
                <ChartDownloadButtonMenu
                  onDownload={handleDownload}
                  buttonLabels={{ png: "Download PNG", jpeg: "Download JPEG" }}
                  sx={{ ml: 1 }}
                  tooltipText={`Download '${chartType}' chart`}
                />
              </Box>
            </Box>

            {/* Chart area */}
            <Box sx={{ position: "relative" }}>
              {loading ? (
                <Spinner size={80} data-testid="spinner" />
              ) : (
                <>
                  {chartType === ProjectRegisterMetrics.MarginalAbatementCostCurve && isMacDataLoaded && (
                    <div>
                      <MarginalAbatementCostCurve ref={macCurveRef} data={macData} labelKey="locationName" currency={currency} />
                    </div>
                  )}
                  {chartType === ProjectRegisterMetrics.TimelineandFinancialMetrics && isDataLoaded && (
                    <TimelineAndFinancialMetrics
                      ref={timelineFinancialRef}
                      data={tlfMetricsData}
                      width={1100}
                      height={450}
                    />
                  )}
                  {chartType === ProjectRegisterMetrics.MeasureImpactsbyScope && (
                    <Grid container sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                      <MeasureImpactsbyScope
                        ref={measureImpactsRef}
                        data={mibScopeData}
                        currency={currency}
                        groupBy={mapFilterValueToWaterfallGroupBy(chartGroupBy)}
                        groupByOptions={NON_CLIENT_WATERFALL_GROUP_BY_OPTIONS}
                        fitContainerWidth={true}
                        fetchData={fetchWaterfallData}
                      />
                    </Grid>
                  )}
                  {chartType === ProjectRegisterMetrics.InteractiveGlidepath && (
                    portfolioGlidepathData ? (
                      <PortfolioGlidepath data={portfolioGlidepathData} ref={portfolioGlidepathRef} />
                    ) : (
                      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 320 }}>
                        <CircularProgress size={60} sx={{ color: "#E57A1F" }} />
                      </Box>
                    )
                  )}
                </>
              )}
            </Box>
          </Paper>
        </Grid>
      )}

      {/* Portfolio Summary Table — separate card, visible once MAC data is loaded and non-empty */}
      {isMacDataLoaded && macData.length > 0 && (
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid #d8dde3", backgroundColor: "#fff", overflow: "hidden" }}>
            <PortfolioSummaryTable
              macData={macData}
              currency={currency}
              groupBy={mapFilterValueToWaterfallGroupBy(chartGroupBy)}
              chartMeasureOrder={chartMeasureOrder}
            />
          </Paper>
        </Grid>
      )}

    </Grid>
  );
};

export default ProjectRegisterSection;